import { IconCheckCircle, IconInfoOutlined, IconWarning } from '@capitalise/ui-design-system';
import { slideInBottom, slideInTop, slideOutBottom, slideOutTop } from './animations';

export const WidgetTypes = Object.freeze({
  creditScore: 'credit-score',
  questionnaire: 'questionnaire',
});

export const CreditScoreType = Object.freeze({
  creditScoreNone: '0',
  creditScoreA: '1',
  creditScoreB: '2',
  creditScoreC: '3',
  creditScoreD: '4',
  creditScoreE: '5',
  creditScoreF: '6',
  creditScoreG: '7',
  creditScoreH: '8',
});

export const CreditScoreTypeLetter = Object.freeze({
  A: CreditScoreType.creditScoreA,
  B: CreditScoreType.creditScoreB,
  C: CreditScoreType.creditScoreC,
  D: CreditScoreType.creditScoreD,
  E: CreditScoreType.creditScoreE,
  F: CreditScoreType.creditScoreF,
  G: CreditScoreType.creditScoreG,
  H: CreditScoreType.creditScoreH,
});

export const CreditScoreRange = Object.freeze({
  [CreditScoreType.creditScoreA]: '91 to 100',
  [CreditScoreType.creditScoreB]: '81 to 90',
  [CreditScoreType.creditScoreC]: '51 to 80',
  [CreditScoreType.creditScoreD]: '26 to 50',
  [CreditScoreType.creditScoreE]: '16 to 25',
  [CreditScoreType.creditScoreF]: '02 to 15',
  [CreditScoreType.creditScoreG]: '02 to 15',
  [CreditScoreType.creditScoreH]: '02 to 15',
});

export const CreditScoreChartPercent = Object.freeze({
  [CreditScoreType.creditScoreA]: 100,
  [CreditScoreType.creditScoreB]: 90,
  [CreditScoreType.creditScoreC]: 75,
  [CreditScoreType.creditScoreD]: 50,
  [CreditScoreType.creditScoreE]: 25,
  [CreditScoreType.creditScoreF]: 15,
  [CreditScoreType.creditScoreG]: 15,
  [CreditScoreType.creditScoreH]: 15,
});

export const CreditScoreBaseColor = Object.freeze({
  [CreditScoreType.creditScoreA]: 'green',
  [CreditScoreType.creditScoreB]: 'green',
  [CreditScoreType.creditScoreC]: 'warning',
  [CreditScoreType.creditScoreD]: 'warning',
  [CreditScoreType.creditScoreE]: 'danger',
  [CreditScoreType.creditScoreF]: 'danger',
  [CreditScoreType.creditScoreG]: 'danger',
  [CreditScoreType.creditScoreH]: 'danger',
});

export const CreditScoreColor = Object.freeze({
  [CreditScoreType.creditScoreA]: 'green-500',
  [CreditScoreType.creditScoreB]: 'green-300',
  [CreditScoreType.creditScoreC]: 'warning-200',
  [CreditScoreType.creditScoreD]: 'warning-500',
  [CreditScoreType.creditScoreE]: 'danger-600',
  [CreditScoreType.creditScoreF]: 'danger-900',
  [CreditScoreType.creditScoreG]: 'danger-900',
  [CreditScoreType.creditScoreH]: 'danger-900',
});

export const CreditScoreExplainStatus = Object.freeze({
  [CreditScoreType.creditScoreA]: 'Looking good',
  [CreditScoreType.creditScoreB]: 'Looking good',
  [CreditScoreType.creditScoreC]: 'Need some work',
  [CreditScoreType.creditScoreD]: 'Need some work',
  [CreditScoreType.creditScoreE]: 'Needs attention',
  [CreditScoreType.creditScoreF]: 'Needs attention',
  [CreditScoreType.creditScoreG]: 'Needs attention',
  [CreditScoreType.creditScoreH]: 'Needs attention',
});

export const CreditScoreExplainRiskIntro = 'What does ';
export const CreditScoreExplainRiskConclusion = ' mean for my company?';

export const CreditScoreExplainRisk = Object.freeze({
  [CreditScoreType.creditScoreA]: 'minimum risk',
  [CreditScoreType.creditScoreB]: 'low risk',
  [CreditScoreType.creditScoreC]: 'below average risk',
  [CreditScoreType.creditScoreD]: 'above average risk',
  [CreditScoreType.creditScoreE]: 'high risk',
  [CreditScoreType.creditScoreF]: 'maximum risk',
  [CreditScoreType.creditScoreG]: 'maximum risk',
  [CreditScoreType.creditScoreH]: 'maximum risk',
});

export const CreditScoreExplainRiskChart = Object.freeze({
  [CreditScoreType.creditScoreA]: 'Good',
  [CreditScoreType.creditScoreB]: 'Good',
  [CreditScoreType.creditScoreC]: 'Average',
  [CreditScoreType.creditScoreD]: 'Average',
  [CreditScoreType.creditScoreE]: 'Poor',
  [CreditScoreType.creditScoreF]: 'Poor',
  [CreditScoreType.creditScoreG]: 'Poor',
  [CreditScoreType.creditScoreH]: 'Poor',
});

export const CreditScoreExplainRiskIcon = Object.freeze({
  [CreditScoreType.creditScoreA]: IconCheckCircle,
  [CreditScoreType.creditScoreB]: IconCheckCircle,
  [CreditScoreType.creditScoreC]: IconInfoOutlined,
  [CreditScoreType.creditScoreD]: IconInfoOutlined,
  [CreditScoreType.creditScoreE]: IconWarning,
  [CreditScoreType.creditScoreF]: IconWarning,
  [CreditScoreType.creditScoreG]: IconWarning,
  [CreditScoreType.creditScoreH]: IconWarning,
});

export const CreditScoreGaugeAnglePosition = Object.freeze({
  [CreditScoreType.creditScoreA]: 1,
  [CreditScoreType.creditScoreB]: 1,
  [CreditScoreType.creditScoreC]: 0.5,
  [CreditScoreType.creditScoreD]: 0.5,
  [CreditScoreType.creditScoreE]: 0,
  [CreditScoreType.creditScoreF]: 0,
  [CreditScoreType.creditScoreG]: 0,
  [CreditScoreType.creditScoreH]: 0,
});

export const CreditScoreExplainItems = Object.freeze({
  [CreditScoreType.creditScoreA]: [
    'Access to finance highly likely at preferable rates',
    'Suppliers highly likely to offer favourable credit terms',
  ],
  [CreditScoreType.creditScoreB]: [
    'Access to finance likely at preferable rates',
    'Suppliers likely to offer favourable credit terms',
  ],
  [CreditScoreType.creditScoreC]: [
    'Personal guarantee likely to be required for access to finance',
    'Suppliers recommended to  offer limited credit on services',
  ],
  [CreditScoreType.creditScoreD]: [
    'Personal guarantee likely to be required for access to finance',
    'Suppliers recommended to  offer limited credit on services',
  ],
  [CreditScoreType.creditScoreE]: [
    'Access to finance expected to be very challenging',
    'Suppliers recommended to not offer services on credit',
  ],
  [CreditScoreType.creditScoreF]: [
    'Access to finance expected to be very challenging',
    'Suppliers recommended to not offer services on credit',
  ],
  [CreditScoreType.creditScoreG]: [
    'Access to finance expected to be very challenging',
    'Suppliers recommended to not offer services on credit',
  ],
  [CreditScoreType.creditScoreH]: [
    'Access to finance expected to be very challenging',
    'Suppliers recommended to not offer services on credit',
  ],
});

export const EngageTexts = [
  'View the factors impacting your score',
  'Your credit profile as banks and lenders see it',
];

export const EngageTextsBusinessPerformancePartner = [
  'View the factors impacting your score',
  'Free sign up from Business Comparison',
];

export const ErrorTypes = Object.freeze({
  invalidAccessToken: 'invalidAccessToken', //Wrong access token provided
  invalidCompanyNumber: 'invalidCompanyNumber', //Wrong company number provided
  noCreditScore: 'noCreditScore', //No credit score available to given company
  generic: 'generic', //Generic error
});

export const ErrorTypeMessage = Object.freeze({
  [ErrorTypes.invalidAccessToken]: 'Unable to connect to Capitalise',
  [ErrorTypes.invalidCompanyNumber]: 'Your company number might be incorrect',
  [ErrorTypes.noCreditScore]: 'Your company currently doesn’t have a credit score',
  [ErrorTypes.generic]: 'Uknown error',
});

export const ErrorTypeDetailItems = Object.freeze({
  [ErrorTypes.invalidAccessToken]: [
    'There’s some technical problems in the integration',
    'If you are the site owner, please check console log for details',
  ],
  [ErrorTypes.invalidCompanyNumber]: [
    'Check here to see if your company number is correct',
    'Sign up to Capitalise and get notified when it’s available',
  ],
  [ErrorTypes.noCreditScore]: [
    'New company may need a few days to generate score',
    'Sign up to Capitalise and get notified when it’s available',
  ],
  [ErrorTypes.generic]: [],
});

export const AnimationDirection = Object.freeze({
  down: 'down',
  up: 'up',
});

export const Animations = Object.freeze({
  [AnimationDirection.down]: {
    open: slideInBottom,
    close: slideOutBottom,
  },
  [AnimationDirection.up]: {
    open: slideInTop,
    close: slideOutTop,
  },
});

export const AnalyticsEventCategory = Object.freeze({
  widget: 'widget',
});

export const AnalyticsEventAction = Object.freeze({
  creditProfileShown: 'credit-profile-shown',
  creditProfileExplainShown: 'credit-profile-explain-shown',
  creditProfileSignupShown: 'credit-profile-signup-shown',
  callToActionClicked: 'call-to-action-clicked',
  creditProfileErrorShown: 'credit-profile-error-shown',
});

export const AnalyticsServices = Object.freeze({
  all: 3,
  googleAnalytics: 0,
  mixpanel: 2,
});

export const UserTypes = Object.freeze({
  business: 'business',
});

export const CallToActionOrigins = Object.freeze({
  default: 'default',
  signup: 'engage',
  explain: 'explain',
});
